import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence, animate } from 'motion/react';
import { formatCurrencyForDisplay } from '@/lib/utils/currencyFormat';
import type { FloorPlanItemResponse } from '@/features/booking/types/floorPlanTypes';

interface MobileTableListViewProps {
  floorPlanItems: FloorPlanItemResponse[];
  availableTables: string[];
  selectedTable: string | null;
  onTableSelect: (tableNumber: string) => void;
  inCartTables: string[];
  localTables: string[];
  soldOutCartTables?: string[];
  itemType?: string;
  pricePerGuest: number;
  currency?: string;
}

const MobileTableListView: React.FC<MobileTableListViewProps> = ({
  floorPlanItems,
  availableTables,
  selectedTable,
  onTableSelect,
  inCartTables,
  localTables,
  soldOutCartTables = [],
  itemType,
  pricePerGuest,
  currency = 'USD',
}) => {
  const tableCategories = useMemo(() => {
    const map = new Map<number, string[]>();
    floorPlanItems.forEach(item => {
      const size = item.table_size || 2; // fallback
      if (!map.has(size)) {
        map.set(size, []);
      }
      map.get(size)!.push(item.table_number);
    });
    const categories = Array.from(map.entries()).map(([size, tables]) => ({
      table_size: size,
      table_numbers: tables,
    }));
    return categories.sort((a, b) => a.table_size - b.table_size);
  }, [floorPlanItems]);

  const [selectedSize, setSelectedSize] = useState<number | null>(
    tableCategories.length > 0 ? tableCategories[0].table_size : null
  );

  useEffect(() => {
    if (selectedTable) {
      const category = tableCategories.find((cat) =>
        cat.table_numbers.includes(selectedTable)
      );
      if (category) {
        setSelectedSize(category.table_size);
      }
    }
  }, [selectedTable, tableCategories]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const listContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedSize && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const element = document.getElementById(`mobile-tab-${selectedSize}`);
      if (element) {
        const elementRect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        if (elementRect.left < containerRect.left + 20 || elementRect.right > containerRect.right - 20) {
          const targetScrollLeft = container.scrollLeft + (elementRect.left - containerRect.left) - (container.clientWidth / 2) + (elementRect.width / 2);
          animate(container.scrollLeft, targetScrollLeft, {
            onUpdate: latest => { if(container) container.scrollLeft = latest; },
            duration: 0.5,
            ease: [0.32, 0.72, 0, 1]
          });
        }
      }
    }
  }, [selectedSize]);

  useEffect(() => {
    if (selectedTable) {
      const timer = setTimeout(() => {
        const element = document.getElementById(`mobile-table-${selectedTable}`);
        const container = listContainerRef.current;
        if (element && container) {
          const elementRect = element.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          if (elementRect.top < containerRect.top + 20 || elementRect.bottom > containerRect.bottom - 20) {
            const targetScrollTop = container.scrollTop + (elementRect.top - containerRect.top) - (container.clientHeight / 2) + (elementRect.height / 2);
            animate(container.scrollTop, targetScrollTop, {
              onUpdate: latest => { if(container) container.scrollTop = latest; },
              duration: 0.6,
              ease: [0.32, 0.72, 0, 1]
            });
          }
        }
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [selectedTable, selectedSize]);

  if (tableCategories.length === 0) {
    return (
      <div className="text-center py-6 mx-4">
        <p className="text-gray-400 text-sm">No tables available.</p>
      </div>
    );
  }

  const activeCategory = tableCategories.find((cat) => cat.table_size === selectedSize);
  const formatPrice = (amount: number) => formatCurrencyForDisplay(amount, currency);

  return (
    <div className="flex flex-col w-full mx-auto max-w-full px-2" style={{ maxWidth: '100vw' }}>
      {/* Horizontal Tabs */}
      <div className="relative w-full border-b border-gray-800/80 mb-2">
        <div ref={scrollContainerRef} className="flex justify-center overflow-x-auto scrollbar-hide px-2">
          {tableCategories.map((category) => {
            const isSelected = selectedSize === category.table_size;
            const availableCount = category.table_numbers.filter((tn) =>
              availableTables.includes(tn)
            ).length;
            const isSoldOut = availableCount === 0;
            const categoryPrice = category.table_size * pricePerGuest;

            return (
              <button
                key={`tab-${category.table_size}`}
                id={`mobile-tab-${category.table_size}`}
                onClick={() => setSelectedSize(category.table_size)}
                className={`flex flex-col items-center flex-shrink-0 px-4 py-2 text-sm font-medium transition-colors relative whitespace-nowrap
                  ${
                    isSelected
                      ? 'text-white border-b-2 border-blue-500'
                      : isSoldOut
                      ? 'text-gray-600 cursor-not-allowed opacity-50 border-b-2 border-transparent'
                      : 'text-gray-400 hover:text-gray-300 border-b-2 border-transparent'
                  }
                `}
              >
                <span>{category.table_size}-seater</span>
                <span className={`text-xs mt-0.5 ${isSelected ? 'text-emerald-400' : 'text-gray-500'}`}>
                  {formatPrice(categoryPrice)}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* List Container max 1/3 of page height roughly */}
      <div className="rounded-2xl border border-white/10 bg-[#12141c] overflow-hidden mx-2 shadow-lg">
        <div ref={listContainerRef} className="max-h-[308px] overflow-y-auto p-2 scrollbar-hide">
          <AnimatePresence mode="wait">
            {activeCategory && (
              <motion.div
                key={`list-${activeCategory.table_size}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col gap-2"
              >
                {activeCategory.table_numbers.map((tableNumber) => {
                  const isAvailable = availableTables.includes(tableNumber);
                  const isSoldOutInCart = soldOutCartTables.includes(tableNumber);
                  
                  // Show table if available OR sold-out-in-cart
                  if (!isAvailable && !isSoldOutInCart) return null;

                  const inCart = inCartTables.includes(tableNumber);
                  const isPending = localTables.includes(tableNumber);
                  const isAdded = inCart || isPending;
                  const isClickable = isAvailable && !isSoldOutInCart;

                  const tableInfo = floorPlanItems.find(item => item.table_number === tableNumber);
                  const displayName = tableInfo?.name || `${itemType || 'Table'} ${tableNumber}`;

                  return (
                    <motion.button
                      whileTap={isClickable ? { scale: 0.96 } : undefined}
                      key={tableNumber}
                      id={`mobile-table-${tableNumber}`}
                      onClick={() => isClickable && onTableSelect(tableNumber)}
                      className={`
                        w-full rounded-xl flex items-center justify-between p-3 relative
                        transition-all duration-150 border text-left
                        ${
                          isSoldOutInCart
                            ? 'bg-red-900/30 text-red-400 border-red-500/40 cursor-not-allowed opacity-75'
                            : inCart
                            ? 'bg-blue-900/40 text-blue-400 border-blue-500/50 cursor-pointer'
                            : isPending
                            ? 'bg-yellow-900/40 text-yellow-500 border-yellow-500/50 cursor-pointer'
                            : 'bg-white/[0.03] text-gray-300 border-white/5 hover:bg-white/[0.08] cursor-pointer'
                        }
                      `}
                    >
                      {/* Left: Table details */}
                      <div className="flex flex-col flex-1 min-w-0 pr-2">
                        <span className="font-bold text-base truncate">{displayName}</span>
                        {((displayName !== tableNumber && displayName !== `${itemType || 'Table'} ${tableNumber}`) || (tableInfo as any)?.floorPlanName) && (
                          <div className="text-xs mt-0.5 text-gray-400 flex items-center gap-1.5 flex-wrap">
                            {displayName !== tableNumber && displayName !== `${itemType || 'Table'} ${tableNumber}` && (
                              <span>{itemType || 'Table'} {tableNumber}</span>
                            )}
                            {displayName !== tableNumber && displayName !== `${itemType || 'Table'} ${tableNumber}` && (tableInfo as any)?.floorPlanName && (
                              <span className="text-gray-600">|</span>
                            )}
                            {(tableInfo as any)?.floorPlanName && (
                              <span>{(tableInfo as any).floorPlanName}</span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Right: Status badge */}
                      <div className="flex items-center shrink-0">
                        {isSoldOutInCart ? (
                          <span className="text-[10px] px-2 py-1 rounded font-bold uppercase tracking-wider bg-red-500/10 text-red-400 border border-red-500/20 shadow-sm">
                            In Cart · Sold Out
                          </span>
                        ) : isAdded ? (
                           <span className={`text-[10px] px-2 py-1 rounded font-bold uppercase tracking-wider ${
                             inCart ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20 shadow-sm' : 'bg-yellow-500/10 text-yellow-500 border border-yellow-500/20 shadow-sm'
                           }`}>
                             {inCart ? 'In Cart' : 'Pending'}
                           </span>
                        ) : null}
                      </div>
                    </motion.button>
                  );
                })}
              </motion.div>
            )}

            {activeCategory?.table_numbers.filter(tn => availableTables.includes(tn) || soldOutCartTables.includes(tn)).length === 0 && (
              <div className="py-8 text-center text-gray-500 text-sm">
                No {itemType?.toLowerCase() || 'tables'} available for this size.
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default MobileTableListView;
